import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAuth } from "../../AuthContext.tsx";
import { archiveService } from "../../api/service.ts";
import ArchiveUploadDialog from "./ArchiveUploadDialog.tsx";
import CloseIcon from "@mui/icons-material/Close";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useIsMobile } from "../../hooks/useIsMobile.ts";
import PhotoLightbox from "./PhotoLightbox.tsx";

/** Foto dell'archivio storico (generato da scripts/archive-import.py, servito come file statico) */
export interface ArchivePhoto {
    id: string;
    date: string | null;
    time: string | null;
    season: string | null;
    /** foto caricate con solo l'anno (date null) */
    year?: number | null;
    width: number;
    height: number;
}

// produzione: /archive servito da nginx; sviluppo: dal backend (vedi .env.development)
const ARCHIVE_URL = process.env.REACT_APP_ARCHIVE_URL ?? `${process.env.PUBLIC_URL ?? ""}/archive`;
export const archiveThumb = (p: ArchivePhoto) => `${ARCHIVE_URL}/thumb/${p.id}.jpg`;
const archiveWeb = (p: ArchivePhoto) => `${ARCHIVE_URL}/web/${p.id}.jpg`;

export async function loadArchive(): Promise<ArchivePhoto[]> {
    const res = await fetch(`${ARCHIVE_URL}/index.json`, { cache: "no-cache" });
    if (!res.ok) return [];
    const data = await res.json();
    return data.photos ?? [];
}

function longDate(p: ArchivePhoto) {
    if (!p.date) return p.year ? `Anno ${p.year}` : "Data sconosciuta";
    const d = new Date(`${p.date}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return p.time ? `${d}, ore ${p.time}` : d;
}

function shortDate(p: ArchivePhoto) {
    if (!p.date) return p.year ? String(p.year) : "—";
    return new Date(`${p.date}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
}

interface Props {
    open: boolean;
    onClose: () => void;
}

/** Archivio storico delle foto della squadra, raggruppato per stagione */
export default function ArchiveDialog({ open, onClose }: Props) {
    const isMobile = useIsMobile();
    const { isAdmin } = useAuth();
    const [uploadOpen, setUploadOpen] = useState(false);
    const [photos, setPhotos] = useState<ArchivePhoto[] | null>(null);
    const [season, setSeason] = useState<string>("ALL");
    const [viewer, setViewer] = useState<number | null>(null);

    useEffect(() => {
        if (open && photos === null) {
            loadArchive().then(setPhotos).catch(() => setPhotos([]));
        }
    }, [open, photos]);

    const seasons = useMemo(() => {
        const counts = new Map<string, number>();
        (photos ?? []).forEach((p) => counts.set(p.season ?? "?", (counts.get(p.season ?? "?") ?? 0) + 1));
        return [...counts.entries()].sort((a, b) => b[0].localeCompare(a[0]));
    }, [photos]);

    // per stagione (dalla più recente) e, dentro la stagione, nell'ordine dell'indice (data decrescente)
    const visible = useMemo(
        () => (photos ?? [])
            .filter((p) => season === "ALL" || (p.season ?? "?") === season)
            .map((p, i) => ({ p, i }))
            .sort((a, b) => (b.p.season ?? "").localeCompare(a.p.season ?? "") || a.i - b.i)
            .map(({ p }) => p),
        [photos, season]
    );

    const deletePhoto = async (photo: ArchivePhoto) => {
        if (!window.confirm(`Eliminare dall'archivio la foto del ${shortDate(photo)}?`)) return;
        try {
            await archiveService.remove(photo.id);
            setPhotos((list) => (list ?? []).filter((p) => p.id !== photo.id));
        } catch {
            alert("Eliminazione non riuscita");
        }
    };

    // gruppi per stagione nell'ordine delle foto (dalla più recente)
    const groups = useMemo(() => {
        const g: { season: string; items: { photo: ArchivePhoto; index: number }[] }[] = [];
        visible.forEach((photo, index) => {
            const key = photo.season ?? "?";
            const last = g[g.length - 1];
            if (last && last.season === key) last.items.push({ photo, index });
            else g.push({ season: key, items: [{ photo, index }] });
        });
        return g;
    }, [visible]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isMobile}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "primary.main", color: "white" }}>
                <PhotoLibraryIcon />
                <Box sx={{ flexGrow: 1 }}>
                    Archivio storico
                    {photos && <Typography component="span" variant="body2" sx={{ ml: 1.5, opacity: 0.85 }}>{photos.length} foto</Typography>}
                </Box>
                {isAdmin && (
                    <Button
                        variant="contained"
                        color="inherit"
                        size="small"
                        startIcon={<AddPhotoAlternateIcon />}
                        onClick={() => setUploadOpen(true)}
                        sx={{ color: "primary.main", mr: 1 }}
                    >
                        {isMobile ? "Aggiungi" : "Aggiungi foto"}
                    </Button>
                )}
                <IconButton onClick={onClose} sx={{ color: "white" }} aria-label="Chiudi"><CloseIcon /></IconButton>
            </DialogTitle>

            {photos && photos.length > 0 && (
                <Stack direction="row" sx={{ px: 2, py: 1.5, gap: 1, overflowX: "auto", borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                    <Chip label="Tutte" color={season === "ALL" ? "primary" : "default"} onClick={() => setSeason("ALL")} />
                    {seasons.map(([s, n]) => (
                        <Chip key={s} label={`${s} (${n})`} color={season === s ? "primary" : "default"} onClick={() => setSeason(s)} />
                    ))}
                </Stack>
            )}

            <DialogContent sx={{ bgcolor: "grey.50" }}>
                {photos === null ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
                ) : photos.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>L'archivio è ancora vuoto</Typography>
                ) : (
                    groups.map((g) => (
                        <Box key={g.season} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                Stagione {g.season} <Typography component="span" variant="body2" color="text.secondary">· {g.items.length} foto</Typography>
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(auto-fill, minmax(180px, 1fr))" }, gap: 1 }}>
                                {g.items.map(({ photo, index }) => (
                                    <Box
                                        key={photo.id}
                                        onClick={() => setViewer(index)}
                                        sx={{
                                            position: "relative", aspectRatio: "1 / 1", borderRadius: 2, overflow: "hidden", cursor: "zoom-in",
                                            bgcolor: "grey.300", "&:hover img": { transform: "scale(1.05)" },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            loading="lazy"
                                            src={archiveThumb(photo)}
                                            alt={`Foto del ${shortDate(photo)}`}
                                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.3s" }}
                                        />
                                        {isAdmin && (
                                            <Tooltip title="Elimina dall'archivio">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Elimina foto"
                                                    onClick={(e) => { e.stopPropagation(); deletePhoto(photo); }}
                                                    sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(0,0,0,0.45)", color: "white", "&:hover": { bgcolor: "error.main" } }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Typography
                                            variant="caption"
                                            sx={{ position: "absolute", left: 0, right: 0, bottom: 0, px: 1, py: 0.5, color: "white", fontWeight: 600, background: "linear-gradient(transparent, rgba(0,0,0,0.65))" }}
                                        >
                                            {shortDate(photo)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))
                )}
            </DialogContent>

            <ArchiveUploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onUploaded={() => setPhotos(null)}
            />

            <PhotoLightbox
                open={viewer !== null}
                photos={visible.map((p) => ({
                    src: archiveWeb(p),
                    alt: `Foto del ${shortDate(p)}`,
                    title: longDate(p),
                    subtitle: p.season ? `Stagione ${p.season}` : undefined,
                }))}
                index={viewer ?? 0}
                onIndexChange={setViewer}
                onClose={() => setViewer(null)}
            />
        </Dialog>
    );
}
